import React from 'react';
import { Button, SxProps, Theme, useTheme } from '@mui/material';
import { styled } from '@mui/material/styles';

export type PepwiseButtonVariant = 'solid' | 'bordered' | 'text';

interface PepwiseButtonColors {
  solidBg: string;
  solidText: string;
  solidHoverBg: string;
  borderedText: string;
  borderedBorder: string;
  borderedHover: string;
  borderedHoverText: string;
  textColor: string;
  textHover: string;
  disabledBg: string;
  disabledSolidText: string;
  disabledText: string;
  disabledBorder: string;
}

export interface PepwiseButtonProps {
  label: React.ReactNode;
  variantType?: PepwiseButtonVariant;
  disabled?: boolean;
  fullWidth?: boolean;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  type?: 'button' | 'submit' | 'reset';
  colors?: PepwiseButtonColors;
  sx?: SxProps<Theme>;
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
}

interface StyledPepwiseButtonProps {
  buttonvariant: PepwiseButtonVariant;
  buttoncolors: PepwiseButtonColors;
}

const StyledPepwiseButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== 'buttonvariant' && prop !== 'buttoncolors',
})<StyledPepwiseButtonProps>(({ buttonvariant, buttoncolors }) => {
  const sharedStyles = {
    width: 'fit-content',
    minWidth: 'auto',
    height: 44,
    padding: '10px 24px',
    borderRadius: '4px',
    textTransform: 'uppercase',
    fontWeight: 600,
    fontSize: '14px',
    lineHeight: 1.2,
    boxShadow: 'none',
    whiteSpace: 'nowrap',
    transition: 'all 0.2s ease-in-out',
    '&:hover': {
      boxShadow: 'none',
    },
    '&:active': {
      boxShadow: 'none',
    },
    '&.Mui-disabled': {
      cursor: 'not-allowed',
    },
  };

  const variantStyles = {
    solid: {
      backgroundColor: buttoncolors.solidBg,
      color: buttoncolors.solidText,
      border: '1px solid transparent',
      '&:hover': {
        backgroundColor: buttoncolors.solidHoverBg,
      },
      '&.Mui-disabled': {
        backgroundColor: buttoncolors.disabledBg,
        color: `${buttoncolors.disabledSolidText} !important`,
      },
    },
    bordered: {
      color: buttoncolors.borderedText,
      border: `1px solid ${buttoncolors.borderedBorder}`,
      '&:hover': {
        borderColor: buttoncolors.borderedHover,
        color: buttoncolors.borderedHoverText,
      },
      '&.Mui-disabled': {
        backgroundColor: 'transparent',
        color: `${buttoncolors.disabledText} !important`,
        border: `1px solid ${buttoncolors.disabledBorder}`,
      },
    },
    text: {
      backgroundColor: 'transparent',
      color: buttoncolors.textColor,
      border: '1px solid transparent',
      minWidth: 'auto',
      '&:hover': {
        color: buttoncolors.textHover,
      },
      '&.Mui-disabled': {
        color: `${buttoncolors.disabledText} !important`,
        border: '1px solid transparent',
      },
    },
  };

  return {
    ...sharedStyles,
    ...variantStyles[buttonvariant],
  };
});

const PepwiseCustomButton: React.FC<PepwiseButtonProps> = ({
  label,
  variantType = 'solid',
  disabled = false,
  fullWidth = false,
  onClick,
  type = 'button',
  colors,
  sx,
  startIcon,
  endIcon,
}) => {
  const theme = useTheme();

  const themeButtonColors = theme.palette.buttons.primary as PepwiseButtonColors;

  const mergedColors: PepwiseButtonColors = {
    ...themeButtonColors,
    ...colors,
  };

  return (
    <StyledPepwiseButton
      type={type}
      disabled={disabled}
      fullWidth={fullWidth}
      onClick={onClick}
      startIcon={startIcon}
      endIcon={endIcon}
      buttonvariant={variantType}
      buttoncolors={mergedColors}
      sx={sx}
    >
      {label}
    </StyledPepwiseButton>
  );
};

export default PepwiseCustomButton;
